"use client"

import createMenuStructure, { MenuItem as ApiMenuItem, Menu as MenuItem } from "@/components/create-menu-structure"
import { Images } from "@/constants/images"
import { getMainMenu, getSettings } from "@/lib/navigation-services"
import "@/styles/globals.css"
import { CloseOutlined } from "@ant-design/icons"
import { Collapse, CollapseProps, Input, InputRef } from "antd"
import i18next from "i18next"
import Image from "next/image"
import Link from "next/link"
import { usePathname, useRouter, useSearchParams } from "next/navigation"
import React, { useEffect, useRef, useState } from "react"
import { configs } from "../configs"
import useIsMobile from "../lib/config/lib/use-is-mobile"
import { DataSettingItem } from "../lib/config/types"
import { DefaultImage } from "../lib/external-components"
import { generateImagePath } from "../lib/utils"
import {
  getHeaderClassName,
  getHeaderContentClassName,
  removeVietnameseDiacritics,
  shouldHeaderBeTransparent,
} from "../utils/header-utils"
import PopupContact from "./popup-contact"
import ScrollToTop from "./scroll-to-top"
import { SvgCarretArrow } from "./svg-component/svg-carret-arrow"
import { SvgSearchIcon } from "./svg-component/svg-search-icon"
interface Menu {
  attributes: {
    items: ApiMenuItem[]
  }
}

type HeaderProps = {
  locale: string
}

const Header: React.FC<HeaderProps> = ({ locale }) => {
  const pathname = usePathname()
  const isMobile = useIsMobile()
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  const [menuData, setMenuData] = useState<Menu>()
  const [isSubMenuOpen, setIsSubMenuOpen] = useState(false)

  const [modalSearch, setModalSearch] = useState(false)
  const [searchValue, setSearchValue] = useState("")
  const [isLoading, setIsLoading] = useState(true)

  const [menuActive, setMenuActive] = useState<string | undefined>(undefined)
  const [subMenu, setSubMenu] = useState<any>()
  const [isScrolled, setIsScrolled] = useState(false)
  const headerRef = useRef<HTMLDivElement>(null)

  const isHomePage = pathname === `/${locale}` || pathname === "/" || pathname === "/en" || pathname === "/vi"

  const shouldBeTransparent = shouldHeaderBeTransparent(pathname, locale, isScrolled)

  const [settingsData, setSettingsData] = useState<DataSettingItem[]>([])
  const [notificationContent, setNotificationContent] = useState<DataSettingItem>()
  const [blogImage, setBlogImage] = useState<string>()
  const [mediaImage, setMediaImage] = useState<string>()
  const [tourImage, setTourImage] = useState<string>()

  const router = useRouter()
  const searchParams = useSearchParams()
  const searchTag = searchParams.get("tag")

  const isActiveItem = (item: MenuItem) => {
    const rawUrl = item?.url
    if (!rawUrl || typeof rawUrl !== "string") return false
    const url = rawUrl.trim()
    if (url.length === 0) return false

    if (pathname === "/") return false

    const base = configs.BASE_URL || ""
    const withBase = `${base}${url}`

    const matches = (candidate: string) => {
      return pathname === candidate || pathname.startsWith(`${candidate}/`) || pathname.startsWith(`${candidate}?`)
    }

    return matches(url) || matches(withBase)
  }

  const isMenuItemActive = (item: MenuItem): boolean => {
    if (!item) return false
    if (isActiveItem(item)) return true
    const children = item.attribute || []
    for (const child of children) {
      if (isActiveItem(child)) return true
      const grandChildren = child.attribute || []
      for (const grand of grandChildren) {
        if (isActiveItem(grand)) return true
      }
    }
    return false
  }

  const getMenuData = async () => {
    try {
      const response = await getMainMenu(configs.HEADER_PAGE_ID[locale as "vi" | "en"], locale)
      setMenuData(response && response)
    } catch (error) {
      console.error("Error fetching data:", error)
    }
  }

  const fetchSetting = async () => {
    try {
      const response = await getSettings()
      const notificationSetting = response.find(
        (item: DataSettingItem) => item.attributes.key === "notificationContentCCA",
      )

      const mediaImageUrl = response.find((item: DataSettingItem) => item.attributes.key === "mediaImageHeader")
      const blogImageUrl = response.find((item: DataSettingItem) => item.attributes.key === "blogImageHeader")
      const tourImageUrl = response.find((item: DataSettingItem) => item.attributes.key === "tourHeaderImage")

      if (notificationSetting) {
        setNotificationContent(notificationSetting)
      }

      if (mediaImageUrl) {
        setMediaImage(mediaImageUrl.attributes.value)
      }

      if (blogImageUrl) {
        setBlogImage(blogImageUrl.attributes.value)
      }

      if (tourImageUrl) {
        setTourImage(tourImageUrl.attributes.value)
      }

      setSettingsData(response)
    } catch (error) {
      console.error("Error fetching setting data:", error)
    }
  }

  const structuredMenu = createMenuStructure(menuData && menuData.attributes.items)

  const changeLanguage = (lang: string) => {
    i18next.changeLanguage(lang)
    localStorage.setItem("language", lang)
    router.push(`/${lang}`)
  }

  const handleMouseLeave = () => {
    setMenuActive(undefined)
    setIsSubMenuOpen(false)
  }

  const handleMenuDesktop = (menu: string) => {
    if (menuActive === menu) {
      setMenuActive(undefined)
      setIsSubMenuOpen(false)
    } else {
      setMenuActive(menu)
      setIsSubMenuOpen(true)
    }
  }

  const inputRef = useRef<InputRef>(null)

  useEffect(() => {
    if (modalSearch && inputRef.current) {
      inputRef.current.focus()
    }
  }, [modalSearch])

  const getImageSrc = (path: string | null | undefined, fallback: string = DefaultImage) => {
    if (!path) return fallback

    try {
      const generatedPath = generateImagePath(path, "medium")
      return generatedPath || fallback
    } catch (error) {
      console.error("Error generating image path:", error)
      return fallback
    }
  }

  const SearchModal = () => {
    const styleInput = {
      affixWrapper: {
        padding: "0px",
        height: 48,
        borderRadius: 12,
        borderColor: "#D1D3D4",
        overflow: "hidden",
      },
      input: { padding: "12px 0px 12px 8px", color: "#8B8B90" },
      suffix: { padding: "16px" },
      prefix: { paddingLeft: "16px" },
    }

    return (
      <div
        className={`fixed inset-0 bg-white z-[9999] p-4 flex flex-col sm:flex-row sm: gap-4 transform transition-all duration-500 ease-out will-change-transform ${
          modalSearch ? "translate-x-0" : "translate-x-full"
        }`}
        onKeyDown={e => {
          if (e.key === "Escape") {
            setModalSearch(false)
          }
        }}
      >
        <div onClick={() => setModalSearch(false)}>
          <SvgCarretArrow
            className="w-4 h-4 "
            fillColor="#242F3E"
            style={{ rotate: "180deg", height: 16, width: 16 }}
          />
        </div>

        <Input
          ref={inputRef}
          styles={styleInput}
          suffix={searchValue && <CloseOutlined style={{ color: "#8E8E93" }} onClick={() => setSearchValue("")} />}
          className="w-full sm:w-[37.5rem] mx-auto flex"
          placeholder={`${
            locale === "en" ? "Type a destination, e.g. Saigon" : "Nhập tên địa điểm bạn muốn đến, ví dụ: Sài Gòn"
          }`}
          onChange={e => setSearchValue(removeVietnameseDiacritics(e.target.value))}
          prefix={<SvgSearchIcon className="" fillColor="#4F4F4F" />}
          value={searchValue}
          onPressEnter={() => {
            router.push(
              locale === "en"
                ? `/all-tours?search=${searchValue}&#all-tours`
                : `/vi/all-tours-vi?search=${searchValue}&#all-tours`,
            )
            setModalSearch(false)
          }}
          onKeyDown={e => {
            if (e.key === "Escape") {
              setModalSearch(false)
            }
          }}
          autoFocus={true}
        />
      </div>
    )
  }

  const activeMenu = structuredMenu?.find(item => item.title.toLowerCase() === menuActive?.toLowerCase())

  const SubMenuMobile = () => {
    const subMenuItems: CollapseProps["items"] = activeMenu?.attribute?.map((item: any) => {
      return {
        key: item.id,
        label: (
          <div className="text-base leading-5 font-semibold text-[#333238] font-Josefin_Sans">
            {item.attribute && item.attribute.length > 0 ? (
              <Link
                key={item.id}
                className="hover:text-[#0b9444]"
                href={item.url ?? ""}
                onClick={e => {
                  e.stopPropagation()
                }}
              >
                {item.title}
              </Link>
            ) : (
              <Link className="hover:text-[#0b9444]" href={item.url ?? ""}>
                {item.title}
              </Link>
            )}
          </div>
        ),
        children:
          item.attribute && item.attribute.length > 0 ? (
            <div className="flex flex-col gap-4 text-[#333238] text-sm">
              {item.attribute.map((subItem: any, index: number) => (
                <Link
                  href={
                    subItem.url.length > 0
                      ? locale === "en"
                        ? `/tours?tag=${subItem.url}`
                        : `/vi/tours?tag=${subItem.url}`
                      : `/tours`
                  }
                  key={index}
                  className="font-Josefin_Sans hover:text-[#0b9444]"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {subItem.title}
                </Link>
              ))}
            </div>
          ) : null,
        styles: { header: { padding: 0 }, body: { padding: 16, paddingBottom: 0 } },
        showArrow: item.attribute && item.attribute.length > 0,
        collapsible: item.attribute && item.attribute.length === 0 && "disabled",
      }
    })

    return (
      <div
        className={`w-full bg-white transform transition-all duration-300 ease-out will-change-transform h-[calc(100vh-66px)] fixed top-[66px] p-4 flex flex-col gap-6 ${
          isMenuOpen && isSubMenuOpen == true ? "translate-x-0 z-30" : "translate-x-full z-30"
        }`}
      >
        <div onClick={() => setIsSubMenuOpen(false)}>
          <SvgCarretArrow className="w-4 h-4 rotate-180" fillColor="#242F3E" style={{ height: 16, width: 16 }} />
        </div>

        <p className="uppercase text-xl leading-6 font-medium text-[#242F3E]">{activeMenu?.title}</p>

        <div className="flex flex-col gap-6">
          <Collapse
            ghost
            className="flex gap-6 flex-col"
            items={subMenuItems}
            expandIconPosition="end"
            style={{ padding: 0 }}
            expandIcon={({ isActive }) => (
              <SvgCarretArrow
                className={`w-4 h-4 ${isActive ? "-rotate-90" : "rotate-90"} `}
                fillColor="#242F3E"
                style={{ height: 16, width: 16 }}
              />
            )}
          />

          {/* <div>
            <div className="w-full overflow-hidden rounded-lg relative h-[176px]">
              <Image
                src={generateImagePath(`/uploads/13281aff6d6566a519a46ceafb6679c8_303a2affdc.webp`, "medium")}
                alt="banner"
                fill
                className="w-full object-cover transform rounded-lg transition-transform duration-500 ease-in-out hover:scale-110"
              />
            </div>
          </div> */}
        </div>
      </div>
    )
  }

  const LanguageSwitch = () => {
    const activeStyle = shouldBeTransparent ? "bg-white text-[#242F3E]" : "bg-[#3ab54b] text-white"
    const inactiveStyle = shouldBeTransparent ? "text-white" : "bg-transparent text-[#242F3E]"

    return (
      <div
        className={`relative flex flex-row w-[100px] p-0.5 rounded-full overflow-hidden transition-all duration-300 ease-in-out ${
          shouldBeTransparent ? "bg-[#8f8f8f]/50" : "bg-[#C9E4A5]"
        }`}
      >
        <button
          onClick={() => changeLanguage("en")}
          className={`w-1/2 uppercase font-bold text-sm leading-5 text-center py-1 z-10 duration-500 transition-all cursor-pointer rounded-full ${
            locale === "en" ? activeStyle : inactiveStyle
          }`}
        >
          EN
        </button>
        <button
          onClick={() => changeLanguage("vi")}
          className={`flex items-center justify-center w-1/2 py-1 rounded-full ${
            locale === "vi" ? activeStyle : inactiveStyle
          }`}
        >
          VI
        </button>
      </div>
    )
  }

  const MobileLanguageSwitch = () => {
    return (
      <div className="relative flex flex-row w-[100px] p-0.5 rounded-full overflow-hidden transition-all duration-300 ease-in-out bg-[#B3E1B9]">
        <button
          onClick={() => (window.location.href = "/en")}
          className={`w-1/2 uppercase font-bold text-sm leading-5 text-center py-1 z-10 duration-500 transition-all cursor-pointer rounded-full ${
            locale === "en" ? "bg-[#0C9443] text-white" : "bg-transparent text-black"
          }`}
        >
          EN
        </button>
        <button
          onClick={() => (window.location.href = "/vi")}
          className={`flex items-center justify-center w-1/2 py-1 rounded-full ${
            locale === "vi" ? "bg-[#0C9443] text-white" : "bg-transparent text-black"
          }`}
        >
          VI
        </button>
      </div>
    )
  }

  const hanldeOpenSubMenu = (attributes: any) => {
    setIsSubMenuOpen(true)
    setSubMenu(attributes)
  }

  const MenuMobile = () => {
    return (
      <>
        <div
          className={`w-full bg-white transform transition-all duration-300 ease-out will-change-transform h-[calc(100vh-66px)] fixed top-[66px] ${
            isMenuOpen ? "translate-x-0 z-20" : "translate-x-full z-20"
          }`}
        >
          <div className="pt-4 pb-[29px] px-4 w-full flex justify-end">{MobileLanguageSwitch()}</div>

          <div className="flex flex-col w-full">
            {structuredMenu &&
              structuredMenu.map((item, index) => (
                <React.Fragment key={item.id || `${index}-${item.title}`}>
                  {item.attribute && item.attribute.length > 0 ? (
                    <div
                      className="flex flex-row justify-between p-4 items-center"
                      onClick={() => {
                        hanldeOpenSubMenu(item)
                        setMenuActive(item.title)
                      }}
                    >
                      <p
                        className={`uppercase text-xl leading-6 text-[#242F3E] ${
                          isMenuItemActive(item) ? "font-bold" : "font-medium"
                        }`}
                      >
                        {item.title}
                      </p>
                      <SvgCarretArrow className="w-4 h-4" fillColor="#242F3E" />
                    </div>
                  ) : (
                    <Link
                      href={item.url}
                      className={`uppercase p-4 text-xl leading-6 text-[#242F3E] ${
                        isActiveItem(item) ? "font-bold" : "font-medium"
                      }`}
                    >
                      {item.title}
                    </Link>
                  )}
                </React.Fragment>
              ))}
          </div>
        </div>
        {SubMenuMobile()}
      </>
    )
  }

  useEffect(() => {
    setIsMenuOpen(false)
    setIsSubMenuOpen(false)
    setMenuActive(undefined)
  }, [pathname, searchTag])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (headerRef.current && !headerRef.current.contains(event.target as Node)) {
        setMenuActive(undefined)
        setIsSubMenuOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  useEffect(() => {
    setIsLoading(false)
    getMenuData()
    fetchSetting()
  }, [])

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY
      setIsScrolled(scrollTop > 50)
    }

    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  // useEffect(() => {
  //   getActiveItemId()
  // }, [pathname, structuredMenu])

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="loader flex flex-row gap-4 p-12">
          <Image src={Images.logoCCAGreen} alt="Logo" className="object-contain" />
        </div>
      </div>
    )
  }

  return (
    // <div className="min-h-[65px] sm:min-h-[70px]" ref={headerRef}>
    <>
      <div id="site-header" className={getHeaderClassName(pathname, locale, isScrolled, isMenuOpen)}>
        <div className={getHeaderContentClassName(pathname, locale, isScrolled, isMenuOpen)}>
          <Link href={`/${locale}`} className="">
            {!shouldBeTransparent || isMenuOpen ? (
              <div className="relative h-[34px] w-[135px] md:w-[146px] md:h-[50px]">
                <Image src={Images.logoCCAGreen} alt="Logo" fill className="object-contain" sizes="100vw" priority />
              </div>
            ) : (
              <div className="relative h-[34px] w-[135px] md:w-[146px] md:h-[50px]">
                <Image
                  src={Images.logoCCATransparent}
                  alt="Logo"
                  fill
                  className="object-contain"
                  sizes="100vw"
                  priority
                />
              </div>
            )}
          </Link>

          <div className="hidden sm:flex gap-3">
            {structuredMenu?.map(item => {
              if (item.attribute && item.attribute.length > 0) {
                return (
                  <div
                    key={item.id}
                    className="relative flex flex-row items-center px-4 py-2 cursor-pointer"
                    onClick={() => handleMenuDesktop(item.title)}
                    onMouseEnter={() => {
                      setMenuActive(item.title)
                      setIsSubMenuOpen(true)
                    }}
                    onMouseLeave={() => {
                      handleMouseLeave()
                    }}
                  >
                    <p
                      className={`uppercase text-[14px] leading-[22px] pr-2.5 hover:opacity-100 transition-all duration-300 ${
                        shouldBeTransparent ? "text-white hover:text-white" : "text-black hover:text-gray-700"
                      } ${
                        menuActive == item.title || isMenuItemActive(item)
                          ? "opacity-100 font-bold"
                          : "opacity-80 font-medium"
                      }`}
                    >
                      {item.title}
                    </p>
                    <SvgCarretArrow
                      className="w-3 h-3 rotate-90"
                      fillColor={shouldBeTransparent ? "#fff" : "#000000"}
                    />
                    {menuActive === item.title && true && (
                      <div
                        className="absolute top-full left-0 z-[9999] transform transition-all duration-500 opacity-100 bg-white shadow-md rounded-sm min-w-[13.875rem]"
                        onMouseLeave={handleMouseLeave}
                      >
                        <div className="flex h-full justify-between max-w-[1440px] mx-auto pt-6 pl-6 pr-[19px] pb-[10px] min-w-max">
                          <div className="w-full">
                            {activeMenu?.attribute && (
                              <div className="grid grid-cols-1 gap-2">
                                {activeMenu.attribute.map((subItem, index: number) => (
                                  <div key={subItem.id} className="flex flex-col gap-2">
                                    <Link
                                      href={configs.BASE_URL + subItem.url}
                                      className={`group relative flex items-center ${
                                        index === (activeMenu?.attribute?.length ?? 0) - 1 &&
                                        "mt-[17px] pt-[7px] border-t-[0.5px] border-[#4F4F4FCC]"
                                      }`}
                                    >
                                      <span className="absolute left-0 opacity-0 transform scale-0 transition-all duration-300 ease-in-out group-hover:opacity-100 group-hover:scale-100">
                                        <Image
                                          src={Images.iconBicycleGreen}
                                          alt="Logo"
                                          width={24}
                                          height={24}
                                          className="h-6 object-contain"
                                        />
                                      </span>
                                      <span
                                        className={`${
                                          index === (activeMenu?.attribute?.length ?? 0) - 1
                                            ? "font-light italic"
                                            : "font-normal"
                                        } text-base leading-6 text-[#333238] transition-all duration-300 ease-in-out group-hover:pl-8 group-hover:text-[#0b9444] whitespace-nowrap block w-full`}
                                      >
                                        {subItem.title}
                                      </span>
                                    </Link>
                                    {subItem.attribute &&
                                      subItem.attribute.map(child => (
                                        <Link
                                          key={child.id}
                                          href={
                                            child.url.length > 0
                                              ? locale === "en"
                                                ? `/tours?tag=${child.url}`
                                                : `/vi/tours?tag=${child.url}`
                                              : `/tours`
                                          }
                                          className="group relative flex items-center"
                                        >
                                          <span className="absolute left-0 opacity-0 transform scale-0 transition-all duration-300 ease-in-out group-hover:opacity-100 group-hover:scale-100">
                                            <Image
                                              src={Images.iconBicycleGreen}
                                              alt="Logo"
                                              width={24}
                                              height={24}
                                              className="h-6 object-contain "
                                            />
                                          </span>
                                          <span className="text-lg leading-6 text-[#333238] font-normal transition-all duration-300 ease-in-out group-hover:pl-8 group-hover:text-[#0b9444] whitespace-nowrap block w-full">
                                            {child.title}
                                          </span>
                                        </Link>
                                      ))}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )
              } else {
                return (
                  <Link
                    key={item.id}
                    href={configs.BASE_URL + item.url}
                    className={`uppercase text-[14px] leading-[22px] px-4 py-2 hover:opacity-100 transition-all duration-300 ${
                      shouldBeTransparent ? "text-white hover:text-white" : "text-black hover:text-gray-700"
                    } ${isActiveItem(item) ? "opacity-100 font-bold" : "opacity-80 font-medium"}`}
                  >
                    {item.title}
                  </Link>
                )
              }
            })}
          </div>
          <div className="flex flex-row gap-6 sm:gap-3 items-center">
            <div className="hidden sm:flex">{LanguageSwitch()}</div>

            <Image
              src={Images.searchIcon}
              alt="Logo"
              width={24}
              height={24}
              className={`transition-all duration-300 ease-in-out ${
                !shouldBeTransparent || isMenuOpen ? "brightness-0" : "brightness-100"
              }`}
              onClick={() => {
                setModalSearch(true)
                setIsMenuOpen(false)
                setIsSubMenuOpen(false)
              }}
            />

            <div
              className={`w-14 h-8 relative sm:hidden rounded-full transition-all duration-300 ${
                !shouldBeTransparent || isMenuOpen ? "bg-[#0C9443]" : "bg-[#FFFFFF59]"
              }`}
            >
              <Image
                src={Images.menuColapse}
                alt="Logo"
                width={56}
                height={32}
                onClick={() => {
                  setIsMenuOpen(!isMenuOpen)
                  setIsSubMenuOpen(false)
                }}
                className={`${
                  !isMenuOpen ? "opacity-100" : "opacity-0"
                } absolute transform transition-opacity duration-1000`}
              />
              <Image
                src={Images.menuCloseColapse}
                alt="Logo"
                width={56}
                height={32}
                onClick={() => {
                  setIsMenuOpen(!isMenuOpen)
                  setIsSubMenuOpen(false)
                }}
                className={`${
                  isMenuOpen ? "opacity-100" : "opacity-0"
                } absolute transform transition-opacity duration-1000`}
              />
            </div>
          </div>
        </div>
      </div>

      {SearchModal()}
      {MenuMobile()}
      <ScrollToTop />
      <PopupContact isVN={locale === "vi"} />
      {/* </div> */}
    </>
  )
}

export default Header
